import React from "react";
import { NavLink, Outlet, useNavigate, Link, Navigate } from "react-router-dom";
import { useState } from "react";
import {
  LayoutDashboard, Users, Package as PackageIcon, Inbox, LogOut,
  KeyRound, Menu, X, ArrowLeft, Handshake, Settings, FolderOpen, BarChart3
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { PerlaLogo } from "@/components/site/PerlaLogo";
import { ChangePasswordModal } from "@/components/site/ChangePasswordModal";
import { NotificationsBell } from "@/components/site/NotificationsBell";

/**
 * Role-based menu items. Admin sees all, others get filtered view.
 */
const MENUS = {
  admin: [
    { to: "/crm", label: "Dashboard", icon: LayoutDashboard, end: true },
    { to: "/crm/leads", label: "Lead", icon: Inbox },
    { to: "/crm/report", label: "Report", icon: BarChart3 },
    { to: "/crm/users", label: "Utenti", icon: Users },
    { to: "/crm/packages", label: "Pacchetti", icon: PackageIcon },
    { to: "/crm/materiali", label: "Materiali", icon: FolderOpen },
    { to: "/crm/impostazioni", label: "Impostazioni", icon: Settings },
  ],
  seller: [
    { to: "/crm", label: "Dashboard", icon: LayoutDashboard, end: true },
    { to: "/crm/leads", label: "I miei lead", icon: Inbox },
    { to: "/crm/materiali", label: "Materiali", icon: FolderOpen },
    { to: "/crm/impostazioni", label: "Impostazioni", icon: Settings },
  ],
  outreach: [
    { to: "/crm", label: "Dashboard", icon: LayoutDashboard, end: true },
    { to: "/crm/leads", label: "Lead", icon: Inbox },
    { to: "/crm/impostazioni", label: "Impostazioni", icon: Settings },
  ],
  artist: [
    { to: "/crm", label: "Area riservata", icon: LayoutDashboard, end: true },
  ],
};

export const CRMLayout = () => {
  const { user, logout, loading } = useAuth();
  const nav = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [showPwd, setShowPwd] = useState(false);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A]" data-testid="crm-loading">
        <span className="font-display text-3xl text-[#FF5A1F]">Perla Music CRM</span>
      </div>
    );
  }
  if (!user) return <Navigate to="/crm/login" replace />;

  const items = MENUS[user.role] || [];
  const roleLabel = { admin: "Admin", seller: "Venditore", outreach: "Outreach", artist: "Artista" }[user.role] || user.role;

  const handleLogout = () => { logout(); nav("/crm/login"); };

  return (
    <div className="min-h-screen bg-[#FAFAF7] flex" data-testid="crm-layout">
      <aside className="hidden lg:flex flex-col w-64 bg-[#0A0A0A] text-[#FAFAF7] border-r border-[#2A2A2A]">
        <div className="px-6 py-6 border-b border-[#2A2A2A]">
          <PerlaLogo light />
          <div className="mt-3 text-[10px] uppercase tracking-widest text-[#FF5A1F]">CRM · {roleLabel}</div>
        </div>
        <nav className="flex-1 py-6 px-3">
          {items.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.end}
              data-testid={`crm-nav-${it.label.toLowerCase().replace(/\s/g, '-')}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 mb-1 text-sm font-semibold uppercase tracking-wider transition-colors ${
                  isActive ? "bg-[#FF5A1F] text-[#FAFAF7]" : "text-[#FAFAF7]/70 hover:bg-[#1A1A1A]"
                }`
              }
            >
              <it.icon size={16} /> {it.label}
            </NavLink>
          ))}
          {user.role === "admin" && (
            <Link to="/admin" className="mt-6 flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-widest text-[#FAFAF7]/40 hover:text-[#FF5A1F]">
              <Handshake size={14} /> CMS Sito
            </Link>
          )}
        </nav>
        <div className="p-4 border-t border-[#2A2A2A] space-y-2">
          <div className="text-xs text-[#FAFAF7]/50 truncate">{user.name || user.email}</div>
          <button onClick={() => setShowPwd(true)} className="w-full flex items-center gap-2 px-4 py-2 border border-[#2A2A2A] hover:border-[#FF5A1F] hover:text-[#FF5A1F] text-xs font-bold uppercase tracking-widest" data-testid="crm-change-pwd-btn">
            <KeyRound size={14} /> Cambia password
          </button>
          <button onClick={handleLogout} className="w-full flex items-center gap-2 px-4 py-2 border border-[#2A2A2A] hover:border-[#FF5A1F] hover:text-[#FF5A1F] text-xs font-bold uppercase tracking-widest" data-testid="crm-logout-btn">
            <LogOut size={14} /> Logout
          </button>
        </div>
      </aside>

      {/* Mobile topbar */}
      <div className="lg:hidden fixed top-0 inset-x-0 z-40 bg-[#0A0A0A] text-[#FAFAF7] flex items-center justify-between px-4 h-14 border-b border-[#2A2A2A]">
        <PerlaLogo light />
        <div className="flex items-center gap-2">
          <NotificationsBell variant="mobile" />
          <button onClick={() => setMobileOpen((v) => !v)} data-testid="crm-mobile-toggle">
            {mobileOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>
      {mobileOpen && (
        <div className="lg:hidden fixed top-14 inset-x-0 z-40 bg-[#0A0A0A] text-[#FAFAF7] p-4 border-b border-[#2A2A2A]">
          {items.map((it) => (
            <NavLink key={it.to} to={it.to} end={it.end} onClick={() => setMobileOpen(false)}
              className={({ isActive }) => `flex items-center gap-3 px-4 py-3 text-sm uppercase tracking-wider ${isActive ? "text-[#FF5A1F]" : "text-[#FAFAF7]/80"}`}>
              <it.icon size={16} /> {it.label}
            </NavLink>
          ))}
          {user.role === "admin" && (
            <Link to="/admin" onClick={() => setMobileOpen(false)} data-testid="crm-mobile-nav-cms-link"
              className="mt-3 flex items-center gap-2 px-4 py-3 border-2 border-[#FF5A1F]/40 text-[#FF5A1F] text-xs font-bold uppercase tracking-widest">
              <Handshake size={14} /> CMS Sito
            </Link>
          )}
          <button onClick={() => setShowPwd(true)} className="mt-2 w-full flex items-center gap-2 px-4 py-3 border border-[#2A2A2A] text-xs uppercase tracking-widest">
            <KeyRound size={14} /> Cambia password
          </button>
          <button onClick={handleLogout} className="mt-2 w-full flex items-center gap-2 px-4 py-3 border border-[#2A2A2A] text-xs uppercase tracking-widest">
            <LogOut size={14} /> Logout
          </button>
        </div>
      )}

      <main className="flex-1 lg:ml-0 pt-14 lg:pt-0 min-w-0">
        {/* Desktop top-right bar with notifications bell */}
        <div className="hidden lg:flex sticky top-0 z-30 items-center justify-end gap-3 px-8 py-3 bg-[#FAFAF7]/95 backdrop-blur border-b border-[#0A0A0A]/10">
          <span className="text-[10px] uppercase tracking-widest text-[#0A0A0A]/50">CRM · {roleLabel}</span>
          <NotificationsBell variant="desktop" />
        </div>
        <Outlet />
      </main>

      {showPwd && <ChangePasswordModal onClose={() => setShowPwd(false)} />}
    </div>
  );
};
